
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Ad } from '../types';

const AdsPage: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentAdId, setCurrentAdId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [formData, setFormData] = useState<Partial<Ad>>({
    title: '',
    description: '',
    ad_type: 'image',
    image_url: '',
    html_code: '',
    redirect_url: '',
    advertiser_name: '',
    position: 'header',
    size: '728x90',
    status: 'active',
    priority: 1,
    background_color: '#1e293b',
    text_color: '#ffffff'
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    setLoading(true);
    const { data } = await supabase.from('ads').select('*').order('created_at', { ascending: false });
    if (data) setAds(data);
    setLoading(false);
  };

  const handleOpenAdd = () => {
    setFormData({
      title: '',
      description: '',
      ad_type: 'image',
      image_url: '',
      html_code: '',
      redirect_url: '',
      advertiser_name: '',
      position: 'header',
      size: '728x90',
      status: 'active',
      priority: 1,
      background_color: '#1e293b',
      text_color: '#ffffff'
    });
    setImageFile(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleOpenEdit = (ad: Ad) => {
    setFormData(ad);
    setCurrentAdId(ad.id);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = formData.image_url;

      if (formData.ad_type === 'image' && imageFile) {
        const path = `ads/${Date.now()}_${imageFile.name}`;
        const { error: upErr } = await supabase.storage.from('music').upload(path, imageFile);
        if (upErr) throw upErr;
        imageUrl = supabase.storage.from('music').getPublicUrl(path).data.publicUrl;
      }

      const adData = {
        ...formData,
        image_url: formData.ad_type === 'image' ? imageUrl : null,
        html_code: formData.ad_type === 'html' ? formData.html_code : null,
        updated_at: new Date().toISOString()
      };

      if (isEditing && currentAdId) {
        const { error } = await supabase.from('ads').update(adData).eq('id', currentAdId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('ads').insert(adData);
        if (error) throw error;
      }

      setShowModal(false);
      fetchAds();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteAd = async (id: string) => {
    if (!confirm('Terminate this advertising campaign?')) return;
    const { error } = await supabase.from('ads').delete().eq('id', id);
    if (!error) fetchAds();
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-white tracking-tighter uppercase">Campaigns</h2>
          <p className="text-slate-500 font-bold uppercase tracking-[0.3em] text-[10px] mt-2">Monetization Engine</p>
        </div>
        <button 
          onClick={handleOpenAdd}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white px-8 py-4 rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] flex items-center space-x-3 shadow-2xl transition-all border border-white/5 active:scale-95"
        >
          <i className="fas fa-plus"></i>
          <span>New Placement</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {loading ? [1,2,3].map(i => (
          <div key={i} className="glass h-[300px] rounded-[3rem] animate-pulse" />
        )) : ads.map(ad => (
          <div key={ad.id} className="glass p-8 rounded-[3.5rem] border-white/5 shadow-2xl group flex flex-col relative overflow-hidden">
            <div className="flex justify-between items-start mb-6">
              <div>
                <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${
                  ad.status === 'active' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'
                }`}>
                  {ad.status}
                </span>
                <h4 className="text-xl font-black text-white tracking-tighter mt-3 line-clamp-1">{ad.title}</h4>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">{ad.advertiser_name || 'Direct Sale'}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => handleOpenEdit(ad)} className="w-9 h-9 rounded-xl bg-white/5 text-slate-400 hover:text-accent-cyan transition-all">
                  <i className="fas fa-pen text-xs"></i>
                </button>
                <button onClick={() => deleteAd(ad.id)} className="w-9 h-9 rounded-xl bg-white/5 text-slate-400 hover:text-red-500 transition-all">
                  <i className="fas fa-trash text-xs"></i>
                </button>
              </div>
            </div>

            <div className="bg-navy-950 rounded-2xl overflow-hidden border border-white/5 aspect-[16/6] mb-8 relative flex items-center justify-center">
              {ad.ad_type === 'image' ? (
                <img src={ad.image_url || 'https://via.placeholder.com/728x90?text=No+Image'} className="w-full h-full object-cover" alt="" />
              ) : (
                <div className="w-full h-full p-4 overflow-hidden pointer-events-none">
                   <div className="text-[8px] font-mono text-accent-cyan opacity-40 line-clamp-4">
                     {ad.html_code}
                   </div>
                   <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-[10px] font-black uppercase tracking-widest text-white">HTML Script Active</span>
                   </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mt-auto">
              <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Impressions</p>
                <p className="text-lg font-black text-white">{ad.impressions.toLocaleString()}</p>
              </div>
              <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Clicks</p>
                <p className="text-lg font-black text-accent-cyan">{ad.clicks.toLocaleString()}</p>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
               <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">{ad.position} • {ad.size}</span>
               <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Priority {ad.priority}</span>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-[#020617]/95 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="glass w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto custom-scrollbar animate-in zoom-in-95 duration-500">
            <div className="p-10 border-b border-white/5 flex justify-between items-center bg-white/[0.01]">
              <div>
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter">{isEditing ? 'Modify Campaign' : 'Create Placement'}</h3>
                <p className="text-slate-500 font-bold uppercase tracking-widest text-[9px] mt-1">Placement Configuration</p>
              </div>
              <button onClick={() => setShowModal(false)} className="w-12 h-12 rounded-2xl bg-white/5 text-slate-400 hover:text-white transition-all">
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-10 space-y-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Ad Title</label>
                  <input required type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-blue/40 font-bold" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Advertiser Name</label>
                  <input type="text" value={formData.advertiser_name} onChange={e => setFormData({...formData, advertiser_name: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-blue/40 font-bold" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Creative Type</label>
                <div className="flex bg-[#020617] p-2 rounded-2xl border border-white/5">
                  <button 
                    type="button" 
                    onClick={() => setFormData({...formData, ad_type: 'image'})}
                    className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${formData.ad_type === 'image' ? 'bg-accent-blue text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
                  >
                    Image Banner
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setFormData({...formData, ad_type: 'html'})}
                    className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${formData.ad_type === 'html' ? 'bg-accent-purple text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
                  >
                    HTML/Script Embed
                  </button>
                </div>
              </div>

              {formData.ad_type === 'image' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Banner Upload</label>
                    <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] || null)} className="w-full bg-white/5 border border-dashed border-white/10 p-4 rounded-2xl text-[10px] text-slate-400 font-bold" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Redirect URL</label>
                    <input type="url" value={formData.redirect_url} onChange={e => setFormData({...formData, redirect_url: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-blue/40 font-bold" placeholder="https://..." />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">HTML / JavaScript Code</label>
                  <textarea 
                    rows={6} 
                    value={formData.html_code} 
                    onChange={e => setFormData({...formData, html_code: e.target.value})} 
                    className="w-full bg-[#020617] border border-white/5 text-accent-cyan px-6 py-4 rounded-2xl focus:ring-2 focus:ring-accent-purple/40 font-mono text-xs" 
                    placeholder="<script async src='...'></script>"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Position</label>
                  <select value={formData.position} onChange={e => setFormData({...formData, position: e.target.value as any})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl font-bold">
                    <option value="header">Header</option>
                    <option value="sidebar">Sidebar</option>
                    <option value="footer">Footer</option>
                    <option value="popup">Popup / Overlay</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Size Variant</label>
                  <select value={formData.size} onChange={e => setFormData({...formData, size: e.target.value})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl font-bold">
                    <option value="728x90">Leaderboard (728x90)</option>
                    <option value="300x250">Medium Rectangle (300x250)</option>
                    <option value="300x600">Half Page (300x600)</option>
                    <option value="160x600">Wide Skyscraper (160x600)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                 <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as any})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl font-bold">
                    <option value="active">Active / Running</option>
                    <option value="paused">Paused</option>
                    <option value="expired">Expired / Offline</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-4">Display Priority (1-10)</label>
                  <input type="number" min="1" max="10" value={formData.priority} onChange={e => setFormData({...formData, priority: parseInt(e.target.value)})} className="w-full bg-[#020617] border border-white/5 text-white px-6 py-4 rounded-2xl font-bold" />
                </div>
              </div>

              <button disabled={uploading} type="submit" className="w-full bg-accent-blue text-white py-5 rounded-[1.8rem] font-black text-xs uppercase tracking-widest shadow-2xl transition-all disabled:opacity-50">
                {uploading ? 'Finalizing Campaign...' : (isEditing ? 'Save Changes' : 'Initialize Campaign')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdsPage;
